import { ApiError } from './fetchUtil'

export type ApiFailureKind = 'network' | 'auth' | 'forbidden' | 'validation' | 'server' | 'unknown'

export type ApiFailure = {
  kind: ApiFailureKind
  message: string
  status?: number
}

type BadRequestError = {
  errorCode?: string
  description?: string
  context?: string
  errorData1?: string
  errorData2?: string
}

type BadRequestResponse = {
  errors?: BadRequestError[]
}

function getNetworkErrorMessage(): string {
  return 'A network error occurred. Please check your connection and try again.'
}

function getUnknownErrorMessage(): string {
  return 'Sorry, something went wrong. Please try again.'
}

function getErrorEntryMessage(
  error: BadRequestError | null | undefined,
  includeErrorCode: boolean
): string | null {
  return (
    error?.errorData2 ||
    error?.errorData1 ||
    error?.description ||
    error?.context ||
    (includeErrorCode ? error?.errorCode : undefined) ||
    null
  )
}

function getBadRequestErrorMessage(body: unknown, includeErrorCode: boolean): string | null {
  return getErrorEntryMessage((body as BadRequestResponse | null)?.errors?.[0], includeErrorCode)
}

export function classifyApiFailure(err: unknown): ApiFailure {
  if (err instanceof TypeError) {
    return {
      kind: 'network',
      message: getNetworkErrorMessage(),
    }
  }

  if (!(err instanceof ApiError)) {
    return {
      kind: 'unknown',
      message: getUnknownErrorMessage(),
    }
  }

  if (err.status === 401) {
    return {
      kind: 'auth',
      status: err.status,
      message: 'Your session has expired. Please sign in again and try again.',
    }
  }

  if (err.status === 403) {
    return {
      kind: 'forbidden',
      status: err.status,
      message:
        'You do not have permission to perform this action. Please contact your administrator.',
    }
  }

  if (err.status === 400) {
    return {
      kind: 'validation',
      status: err.status,
      message: getValidationErrorMessage(err) ?? 'The request could not be completed.',
    }
  }

  if (err.status >= 500) {
    return {
      kind: 'server',
      status: err.status,
      message: 'The server could not complete the request. Please try again.',
    }
  }

  return {
    kind: 'unknown',
    status: err.status,
    message: getUnknownErrorMessage(),
  }
}

export function getApiErrorMessage(err: unknown): string {
  return classifyApiFailure(err).message
}

export function getValidationErrorMessage(err: unknown): string | null {
  if (err instanceof TypeError) {
    return getNetworkErrorMessage()
  }

  if (!(err instanceof ApiError) || err.status !== 400) {
    return null
  }

  return getBadRequestErrorMessage(err.body, true)
}

type ProblemDetailsResponse = {
  title?: string
  detail?: string
  ErrorCode?: string
  ErrorData1?: string
  ErrorData2?: string
}

export function getOrderErrorMessages(err: unknown): string[] {
  if (err instanceof TypeError) {
    return [getNetworkErrorMessage()]
  }

  if (!(err instanceof ApiError)) {
    return []
  }

  if (err.status === 403) {
    return [
      'You do not have permission to place or update orders. Please contact your administrator.',
    ]
  }

  if (err.status !== 400) {
    return []
  }

  const body = err.body as (BadRequestResponse & ProblemDetailsResponse) | null
  const legacyMessage = body?.ErrorData2 || body?.ErrorData1
  if (legacyMessage) {
    return [legacyMessage]
  }

  const messages = (body?.errors ?? [])
    .map(error => getErrorEntryMessage(error, false))
    .filter((message): message is string => message !== null)
  if (messages.length === 0 && body?.title) {
    return [body.title]
  }

  return messages
}
